import { FC, forwardRef, useEffect, useId, useRef, useState } from 'react';
import { ICustomOptionProps } from '@common/types/types';
import { ChevronDown } from '@geist-ui/icons';

import './styles.scss';
import AutomatedAuditLoader from '../AutomatedAuditLoader';
// import { displayName } from 'src/helpers/textWrap';



const displayName = (name: string, maxLength = 60) => {
  if (!name) return name;
  const shouldTruncate = name.length > maxLength
  const displayName = shouldTruncate ? `${name.substring(0, maxLength - 3)}...` : name
  return displayName
}


interface ISelectData {
  value: string;
  label: string;
}

interface Props {
  label?: string;
  data: ISelectData[] | string[];
  placeholder?: string;
  value: any;
  onChange: (value: string | null) => void;
  className?: string;
  withIcon?: boolean;
  itemComponent?: FC<ICustomOptionProps>;
  variant?: 'primary' | 'secondary';
  withBorder?: boolean;
  searchable?: boolean;
  sx?: any;
  error?: string | null;
  autoFocus?: boolean;
  withinPortal?: boolean;
  disabled?: boolean;
  icon?: any;
  namespace?: string;
  loading?: boolean;
  width?:string;
  maxWidth?:string;
}

const maxLength = 60
const Select = forwardRef<HTMLInputElement, Props>(({
  label,
  data,
  placeholder,
  value,
  onChange,
  className,
  withIcon,
  itemComponent,
  variant = 'primary',
  withBorder = false,
  sx = {},
  searchable = false,
  error = null,
  autoFocus = false,
  withinPortal = false,
  disabled = false,
  icon = null,
  namespace = 'default_select_namespace',
  loading = false,
  width = "",
  maxWidth="",
  ...others
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);
  const id = useId()

  // Convert `string[]` to `ISelectData[]`
  let normalizedData = Array.isArray(data) && typeof data[0] === 'string'
    ? (data as string[]).map((label) => ({ value: label, label }))
    : (data as ISelectData[]);

    
    if ( normalizedData?.length == 0) {
      normalizedData.push({ value: "" , label : 'No Data Available' });
    }
    normalizedData = [ { label : 'Select Option' , value : ''} , ...normalizedData ]
  //   // Find the initial selected option
  //   const selectedOption = normalizedData.length > 0 ? normalizedData[0] : { label: placeholder ?? "" , value: "" };
    
  const [selectedValue, setSelectedValue] = useState<ISelectData>()

  useEffect( () => { 
    // Convert `string[]` to `ISelectData[]`
     normalizedData = Array.isArray(data) && typeof data[0] === 'string'
    ? (data as string[]).map((label) => ({ value: label, label }))
    : (data as ISelectData[]);
    // Find the initial selected option
    
    if ( normalizedData?.length == 0) {
      normalizedData.push({ value: "" , label : 'No Data Available' });
      return
    }
    normalizedData = [ { label : 'Select Option' , value : ''} , ...normalizedData ]
    let selectedOption;
    if(!value){
      //  selectedOption = normalizedData.length > 0 ? normalizedData[0] : { label: placeholder ?? "" , value: "" }; 
      selectedOption = normalizedData[0];
    }else{
      const option = normalizedData.find(option => option.value.split('|')[0] === value);     
      selectedOption = option ? option :  normalizedData.length > 0 ? normalizedData[0] : { label: placeholder ?? "" , value: "" }; 
    }
   
    if ( selectedOption ) {
      setSelectedValue(selectedOption)
    }

  } , [value , data ])

  const [focusedIndex, setFocusedIndex] = useState(0);
 

  // Toggle dropdown visibility
  const toggleDropdown = () => setIsOpen(!isOpen);

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selection of a dropdown item
  const handleSelect = (optionValue: string, optionLabel: string) => {
    setSelectedValue({ value: optionValue, label: optionLabel });
    setIsOpen(false);
    onChange(optionValue);
  };

  // Handle key navigation
  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isOpen) return;

    if (event.key === 'ArrowDown' || event.key === 'Tab') {
      event.preventDefault();
      setFocusedIndex((prevIndex) => (prevIndex + 1) % normalizedData.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setFocusedIndex((prevIndex) =>
        prevIndex === 0 ? normalizedData.length - 1 : prevIndex - 1
      );
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selectedOption = normalizedData[focusedIndex];
      handleSelect(selectedOption.value, selectedOption.label);
    } else if (event.key === 'Escape' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      setIsOpen(false);
      buttonRef.current?.focus()
    }
  };

  useEffect( () => {
    // Focus on the item based on the updated focusedIndex
    itemRefs?.current[focusedIndex]?.focus();
  } ,[focusedIndex])

  useEffect(() => {
    if (isOpen) { 
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, focusedIndex, normalizedData]);

  // const [currntOrg, setCurrntOrg] = useState('')
  return (
    <div className="custom-select" ref={dropdownRef} style={ { width : width , maxWidth : maxWidth } }  >
      <label className="custom_select_label" htmlFor={id}>{label}</label>
      <button
        ref={buttonRef}
        id={id}
        disabled={disabled}
        type="button"
        className={`dropdown-toggle ${error ? 'error' : ''}`}
        onClick={() => {
        if ( normalizedData?.length >= 1) {
            toggleDropdown();
            setFocusedIndex(0); // Reset focused index when dropdown opens
          }
        }}
        // onFocus={() => {
        //   if (normalizedData?.length >= 1 && !isOpen) {
        //       toggleDropdown();
        //       setFocusedIndex(0); // Ensure dropdown opens on focus if not already open
        //   }
        // }}
        role="combobox"
        aria-haspopup="listbox"
        aria-controls={`${namespace}_dropdown`}
        aria-expanded={isOpen}
        aria-activedescendant={`option-${focusedIndex}`}
        aria-live="polite"
        {...others}
      >
        
        {selectedValue?.label && displayName( selectedValue?.label , maxLength)}
        {loading ? <AutomatedAuditLoader /> : withIcon && <ChevronDown color="#5b5eff" size={16} />}
      </button>
      {error && (
        <p className="error-message" role="status" aria-live="polite" aria-atomic="true">
          {error}
        </p>
      )}
      {isOpen && (
        <ul
          role="listbox"
          id={`${namespace}_dropdown`}
          tabIndex={-1}
          className="dropdown-menu"
          aria-live="polite"
        >
          
        {
          normalizedData?.length && normalizedData.map((option: ISelectData, index: number) =>{
              const parts = option.value.split('|');
              const _id =  parts[0];
              let orgname = ''; 
              orgname =parts[1] ?? '';
              // if(parts[1]){
              //   if(orgname != currntOrg){
              //     setCurrntOrg(orgname);
              //   }
              // }

            
            return(     
                <>
                
                {
                   orgname ? <strong style={{paddingLeft:'5px'}} aria-label='organisation name'>{displayName(orgname, maxLength) }</strong>  : ''
                }
                <li

                  ref={(el) => (itemRefs.current[index] = el)}
                  role="option"
                  aria-label={option.label}
                  key={_id}
                  id={`option-${focusedIndex}`}
                  // autoFocus={index === focusedIndex}
                  aria-live="polite"
                  aria-selected={index === focusedIndex}
                  className={`dropdown-item  ${selectedValue?.value == option.value? 'active' : '' }`}
                  tabIndex={index === focusedIndex ? 0 : -1}
                  onClick={() => handleSelect(_id, option.label)}
                >
                  {displayName(option.label , maxLength)}
                </li>
                </>
            )
          } 
          
        )
        }
        </ul>
      )}
    </div>
  );
});

Select.displayName = 'Select';

export default Select;
